import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowsLeftRight, X } from "@phosphor-icons/react";
import { photoSrc } from "@/components/Thumb";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkAll } from "@/lib/shrinkImage";

// "Before" shot control for a finished-job photo: upload pairs it, × unpairs it.
export const BeforePhotoControl = ({ item, onChange, compact = false }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const before = item.before_photo?.storage_path ? item.before_photo : null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", (await shrinkAll([file]))[0]);
      const { data } = await api.post(`/completion-photos/${item.id}/before`, fd);
      onChange(data);
      toast.success("Before photo paired — the website shows a swipe slider");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add the before photo");
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = async () => {
    try {
      const { data } = await api.delete(`/completion-photos/${item.id}/before`);
      onChange(data);
      toast.success("Before photo removed");
    } catch {
      toast.error("Could not remove the before photo");
    }
  };

  return (
    <div className={compact ? "" : "mt-3"} data-testid={`completion-before-${item.id}`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={upload}
        data-testid={`completion-before-input-${item.id}`}
      />
      {before ? (
        <div className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-950 p-1.5">
          <img
            src={photoSrc(before, true)}
            alt="Before"
            className="h-9 w-12 shrink-0 rounded object-cover"
            data-testid={`completion-before-thumb-${item.id}`}
          />
          <span className="min-w-0 flex-1 truncate text-[10px] font-bold uppercase tracking-widest text-emerald-300">
            Before / after paired
          </span>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={busy}
            className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 hover:text-zinc-200"
            data-testid={`completion-before-replace-${item.id}`}
          >
            Replace
          </button>
          <button
            type="button"
            onClick={remove}
            title="Remove before photo"
            className="text-zinc-600 hover:text-red-400"
            data-testid={`completion-before-remove-${item.id}`}
          >
            <X size={14} weight="bold" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`completion-before-add-${item.id}`}
          className="flex h-9 w-full items-center justify-center gap-2 rounded-md border border-dashed border-zinc-700 text-[10px] font-bold uppercase tracking-widest text-zinc-500 transition-colors duration-150 hover:border-zinc-500 hover:text-zinc-200 disabled:opacity-50"
        >
          <ArrowsLeftRight size={13} weight="bold" />
          {busy ? "Uploading…" : "Add before photo"}
        </button>
      )}
    </div>
  );
};
